/**
 * 提案メール作成 hook
 * マッチング結果から提案メールをAI生成し、SES経由で送信する
 */
import { fetchAuthSession } from "@aws-amplify/auth";
import { useCallback, useRef, useState } from "react";
import { restClient } from "../lib/rest-client";
import type { MatchingResultItem } from "./use-free-matching-api";
import type {
  SenderEmailItem,
  SenderEmailListResponse,
} from "./use-sender-emails-api";
import type { EmailDocument } from "./useEmailsRestApi";

// REST API ベース URL
const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || "";
const REST_API_BASE_URL = `${API_ENDPOINT}/api`;

// =====================================
// Types
// =====================================

export interface ProposalDraft {
  resultId: string;
  matchedSubject: string;
  totalScore: number;
  aiHighlights: string[] | null;
  fromEmail: string;
  fromName: string;
  toEmail: string;
  toName: string;
  subject: string;
  body: string;
}

interface SendProposalResponse {
  success: boolean;
  message: string;
  messageId: string | null;
}

export interface UseProposalComposerResult {
  draft: ProposalDraft | null;
  isGenerating: boolean;
  isSending: boolean;
  error: string | null;
  startProposal: (result: MatchingResultItem, email: EmailDocument) => void;
  setField: (field: keyof ProposalDraft, value: string) => void;
  sendProposal: () => Promise<boolean>;
  resetDraft: () => void;
  cancelGeneration: () => void;
}

/**
 * 提案メール作成を管理する hook
 * @return {UseProposalComposerResult} 提案メール作成の状態と操作関数
 */
export function useProposalComposer(): UseProposalComposerResult {
  const [draft, setDraft] = useState<ProposalDraft | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const startProposal = useCallback(
    (result: MatchingResultItem, email: EmailDocument) => {
      // 既存のストリーミングをキャンセル
      if (abortRef.current) {
        abortRef.current.abort();
      }

      // ドラフト初期化（送信者は後からAPIで取得）
      setDraft({
        resultId: result.id,
        matchedSubject: result.matchedSubject,
        totalScore: result.totalScore,
        aiHighlights: result.aiHighlights,
        fromEmail: "",
        fromName: "",
        toEmail: email.from.address,
        toName: email.from.name,
        subject: `Re: ${email.subject || ""}`,
        body: "",
      });
      setError(null);
      setIsGenerating(true);

      // SSE ストリーミング開始
      const controller = new AbortController();
      abortRef.current = controller;

      (async () => {
        try {
          const session = await fetchAuthSession();
          const token = session.tokens?.idToken?.toString();
          if (!token) {
            setError("認証トークンの取得に失敗しました");
            setIsGenerating(false);
            return;
          }

          // 認証済み送信者メールをAPIから取得
          let defaultSender: SenderEmailItem | null = null;
          try {
            const senderResponse =
              await restClient.get<SenderEmailListResponse>(
                "/sender-emails",
                // biome-ignore lint/style/useNamingConvention: API query parameter
                { verified_only: "true" },
              );
            const senders = senderResponse.items ?? [];
            defaultSender =
              senders.find(s => s.isDefault) ?? senders[0] ?? null;
          } catch {
            // 送信者取得に失敗した場合はフォールバック
          }

          const senderEmail = defaultSender?.email ?? "";
          const senderName = defaultSender?.displayName ?? "";

          setDraft(prev =>
            prev
              ? { ...prev, fromEmail: senderEmail, fromName: senderName }
              : prev,
          );

          const response = await fetch(
            `${REST_API_BASE_URL}/matching/results/${result.id}/generate-proposal`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                // biome-ignore lint/style/useNamingConvention: HTTP header
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                fromName: senderName,
                fromEmail: senderEmail,
              }),
              signal: controller.signal,
            },
          );

          if (!response.ok) {
            setError(`AI生成エラー: HTTP ${response.status}`);
            setIsGenerating(false);
            return;
          }

          const reader = response.body?.getReader();
          if (!reader) {
            setError("レスポンスの読み取りに失敗しました");
            setIsGenerating(false);
            return;
          }

          const decoder = new TextDecoder();
          let buffer = "";

          let done = false;
          while (!done) {
            const readResult = await reader.read();
            done = readResult.done;
            if (done) break;

            buffer += decoder.decode(readResult.value, { stream: true });

            const lines = buffer.split("\n");
            buffer = lines.pop() || "";

            for (const line of lines) {
              if (!line.startsWith("data: ")) continue;
              const dataStr = line.slice(6);

              try {
                const data = JSON.parse(dataStr);

                if (data.type === "meta") {
                  setDraft(prev =>
                    prev
                      ? {
                          ...prev,
                          toEmail: data.toEmail || prev.toEmail,
                          toName: data.toName || prev.toName,
                          subject: data.subject || prev.subject,
                        }
                      : prev,
                  );
                } else if (data.type === "chunk") {
                  setDraft(prev =>
                    prev ? { ...prev, body: prev.body + data.text } : prev,
                  );
                } else if (data.type === "done") {
                  setIsGenerating(false);
                } else if (data.type === "error") {
                  setError(data.message || "AI生成に失敗しました");
                  setIsGenerating(false);
                }
              } catch {
                // JSON パース失敗は無視
              }
            }
          }

          // ストリーム終了時に isGenerating が true のまま残っている場合
          setIsGenerating(false);
        } catch (e) {
          if ((e as Error).name !== "AbortError") {
            setError(e instanceof Error ? e.message : "AI生成に失敗しました");
            setIsGenerating(false);
          }
        } finally {
          abortRef.current = null;
        }
      })();
    },
    [],
  );

  const setField = useCallback((field: keyof ProposalDraft, value: string) => {
    setDraft(prev => (prev ? { ...prev, [field]: value } : prev));
    // 送信元メールアドレスが変更された場合、名前の更新は呼び出し元で行う
  }, []);

  const sendProposal = useCallback(async (): Promise<boolean> => {
    if (!draft) return false;

    setIsSending(true);
    setError(null);

    try {
      const response = await restClient.post<SendProposalResponse>(
        `/matching/results/${draft.resultId}/send-proposal`,
        {
          fromName: draft.fromName,
          fromEmail: draft.fromEmail,
          toEmail: draft.toEmail,
          toName: draft.toName,
          subject: draft.subject,
          body: draft.body,
        },
      );

      if (!response.success) {
        setError(response.message || "送信に失敗しました");
        return false;
      }

      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : "送信に失敗しました");
      return false;
    } finally {
      setIsSending(false);
    }
  }, [draft]);

  const resetDraft = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setDraft(null);
    setError(null);
    setIsGenerating(false);
    setIsSending(false);
  }, []);

  const cancelGeneration = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setIsGenerating(false);
  }, []);

  return {
    draft,
    isGenerating,
    isSending,
    error,
    startProposal,
    setField,
    sendProposal,
    resetDraft,
    cancelGeneration,
  };
}
